// components/upload/MediaField.jsx
import React, { useRef } from "react";
import { UploadedAsset } from "./UploadedAsset";

/**
 * Generic upload field that shows either an empty "UPLOAD" drop panel
 * or the UploadedAsset with edit/delete controls.
 *
 * Props:
 * - label: string
 * - name: string ("coverImage" | "cardImage" | "introVideo" | etc.)
 * - type: "image" | "video"
 * - src?: string
 * - height: number (px)
 * - accept: string (e.g. "image/*" or "video/*")
 * - uploadFn: (e: ChangeEvent<HTMLInputElement>) => void
 * - deleteFn?: (name: string) => void
 * - onMetadata?: (name: string, durationSeconds: number) => void  // videos
 */
export function MediaField({
  label,
  name,
  type,
  src,
  height = 200,
  accept,
  uploadFn,
  deleteFn,
  onMetadata,
}) {
  // React 16 safe id suffix
  const idSuffixRef = useRef(Math.random().toString(36).slice(2, 8));
  const ids = {
    upload: `upload-${name}-${idSuffixRef.current}`,
    reupload: `reupload-${name}-${idSuffixRef.current}`,
  };

  return (
    <>
      <div className="font-bold my-3">{label}</div>

      {!src ? (
        <div
          className="flex justify-center items-center bg-primary100 border border-dashed border-primary400 rounded-md"
          style={{ height: `${height}px` }}
        >
          <label
            htmlFor={ids.upload}
            className="border border-primary400 text-primary400 px-4 py-2 rounded-sm cursor-pointer font-medium uppercase"
          >
            UPLOAD
          </label>
          <input
            id={ids.upload}
            name={name}
            type="file"
            accept={accept}
            onChange={uploadFn}
            className="hidden"
          />
        </div>
      ) : (
        <UploadedAsset
          name={name}
          src={src}
          type={type}
          reuploadId={ids.reupload}
          uploadFn={uploadFn}
          deleteFn={deleteFn}
          onMetadata={onMetadata}
        />
      )}
    </>
  );
}
